import { Color, Matrix, Rectangle, Texture, type ColorSource, type FillStyle, type Renderer } from "pixi.js";
import { coastalFrameGroups, packCoastalFrames } from "./coastalFrames";
import { loadStylizedEnvironment } from "./stylizedEnvironment";
import { COASTAL_BORDER_ASSETS, COASTAL_BORDER_BASE_BYTES, COASTAL_GROUND_BASE_BYTES,
    COASTAL_GROUND_TILE_SIZE, COASTAL_TERRAIN_ASSETS, COASTAL_TERRAIN_REVISION, COASTAL_GRASS_FILTER } from "./coastalTerrainAssets";

type Ground = "grass" | "sand" | "water";
const groundTextures: Partial<Record<Ground, Texture>> = {};
const borderTextures: Partial<Record<keyof typeof COASTAL_BORDER_ASSETS, Texture>> = {};
let ready = false;
let pavement: Texture | undefined;
let portAsphalt: Texture | undefined;
let portPaint: Texture | undefined;
const refinedTerrain = new URLSearchParams(location.search).get("momoTerrain") !== "legacy";
const groundTileWorldSize: Record<Ground, number> = COASTAL_GROUND_TILE_SIZE;

export const coastalRequested = new URLSearchParams(location.search).get("momoEnvironment") === "coastal";

function canvas(width: number, height: number): HTMLCanvasElement {
    const result = document.createElement("canvas");
    result.width = width;
    result.height = height;
    return result;
}

async function loadImage(name: string): Promise<HTMLImageElement> {
    const image = new Image();
    const ground = name === "grass" || name === "sand" || name === "water";
    image.src = name === "blueberry" ? "/img/momo/coastal/blueberry-52.png" : name === "stump" ? "/img/momo/coastal/stump-45.png"
        : refinedTerrain && ground ? COASTAL_TERRAIN_ASSETS[name as Ground] : `/img/momo/coastal/${name}.png`;
    await image.decode();
    return image;
}

/** One load-time atlas. Natural asset alpha replaces the old circular masks; world sizes stay fixed. */
export async function loadCoastalEnvironment(textures: Record<string, Texture>, renderer: Renderer): Promise<void> {
    const names = [...coastalFrameGroups.map(group => group.image), "grass", "sand", "water"];
    const images = Object.fromEntries(await Promise.all(names.map(async name => [name, await loadImage(name)]))) as Record<typeof names[number], HTMLImageElement>;
    const slots = packCoastalFrames(coastalFrameGroups);
    const usedHeight = Math.max(...slots.map(slot => slot.y + slot.size));
    const atlasCanvas = canvas(2048, 2 ** Math.ceil(Math.log2(usedHeight)));
    const atlasContext = atlasCanvas.getContext("2d");
    if (!atlasContext) throw new Error("Coastal atlas canvas unavailable");
    // Missing IDs abort the preview before any texture dictionary changes.
    for (const group of coastalFrameGroups) for (const name of group.frames) {
        if (!textures[name]) throw new Error(`Coastal source frame missing: ${name}`);
    }
    // Transparent padding and native PNG alpha: no opaque backing plate or legacy silhouette.
    for (const { image, x, y, size } of slots) {
        atlasContext.save();
        if (image === "blueberry") atlasContext.filter = COASTAL_GRASS_FILTER;
        atlasContext.drawImage(images[image], x + 2, y + 2, size - 4, size - 4);
        atlasContext.restore();
    }
    const atlas = Texture.from(atlasCanvas);
    const replacements: Record<string, Texture> = {};
    for (let i = 0; i < coastalFrameGroups.length; i++) {
        const { x, y, size } = slots[i];
        for (const name of coastalFrameGroups[i].frames) {
            replacements[name] = new Texture({
                source: atlas.source,
                frame: new Rectangle(x + 2, y + 2, size - 4, size - 4),
                orig: textures[name].orig.clone(), label: `coastal:${name}`
            });
        }
    }
    const grounds: Partial<Record<Ground, Texture>> = {};
    for (const name of ["grass", "sand", "water"] as const) {
        // Same three 512px GPU textures; refined assets use their full tile instead of mirrored quadrants.
        const tile = canvas(512, 512);
        const context = tile.getContext("2d");
        if (!context) throw new Error("Coastal ground canvas unavailable");
        // A modest, ground-only reduction keeps the map readable without the lime glare.
        if (name === "grass") context.filter = COASTAL_GRASS_FILTER;
        context.imageSmoothingQuality = "high";
        if (refinedTerrain) {
            context.drawImage(images[name], 0, 0, 512, 512);
        } else {
            for (const x of [0, 1]) for (const y of [0, 1]) {
                context.save();
                context.translate(x ? 512 : 0, y ? 512 : 0);
                context.scale(x ? -1 : 1, y ? -1 : 1);
                context.drawImage(images[name], 0, 0, 256, 256);
                context.restore();
            }
        }
        const texture = Texture.from(tile);
        texture.source.style.addressMode = refinedTerrain ? "mirror-repeat" : "repeat";
        grounds[name] = texture;
    }
    if (refinedTerrain) for (const name of ["turf", "surf"] as const) {
        const image = new Image();
        image.src = COASTAL_BORDER_ASSETS[name];
        await image.decode();
        const tile = canvas(512, 256), context = tile.getContext("2d");
        if (!context) throw new Error("Coastal border canvas unavailable");
        if (name === "turf") context.filter = COASTAL_GRASS_FILTER;
        context.imageSmoothingQuality = "high";
        context.drawImage(image, 0, 0, 512, 256);
        const texture = Texture.from(tile);
        texture.source.style.addressModeU = "mirror-repeat";
        texture.source.style.addressModeV = "clamp-to-edge";
        borderTextures[name] = texture;
    }
    const roadImage = new Image();
    roadImage.src = "/img/momo/architecture-v1/pavement.png";
    await roadImage.decode();
    const road = canvas(256, 256);
    const roadContext = road.getContext("2d");
    if (roadContext) {
        roadContext.drawImage(roadImage, 0, 0, 256, 256);
        pavement = Texture.from(road);
        pavement.source.style.addressMode = "mirror-repeat";
    }
    const portImage = new Image();
    portImage.src = "/img/momo/terrain-v7/port-asphalt.png";
    await portImage.decode();
    const portTile = canvas(512, 512), portContext = portTile.getContext("2d")!;
    portContext.drawImage(portImage, 0, 0, 512, 512);
    portAsphalt = Texture.from(portTile);
    portAsphalt.source.style.addressMode = "mirror-repeat";
    // Worn paint shares world coordinates across every bay and lane.
    const paintTile = canvas(128, 128), paintContext = paintTile.getContext("2d")!;
    paintContext.fillStyle = "rgba(255,255,255,.87)";
    paintContext.fillRect(0, 0, 128, 128);
    let seed = 51;
    const random = (): number => { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; return seed / 4294967296; };
    for (let i = 0; i < 520; i++) {
        const x = random() * 128, y = random() * 128;
        paintContext.clearRect(x, y, 0.6 + random() * 1.4, 0.6 + random() * 1.8);
    }
    portPaint = Texture.from(paintTile);
    portPaint.source.style.addressMode = "repeat";
    await Promise.all([atlas, ...Object.values(grounds), ...Object.values(borderTextures),
        ...(pavement ? [pavement] : []), portAsphalt, portPaint].map(texture => renderer.prepare.upload(texture)));
    Object.assign(textures, replacements);
    Object.assign(groundTextures, grounds);
    ready = true;
    document.documentElement.dataset.momoTerrainRevision = refinedTerrain ? String(COASTAL_TERRAIN_REVISION) : "3";
    document.documentElement.dataset.momoTerrainBaseBytes = String(COASTAL_GROUND_BASE_BYTES);
    document.documentElement.dataset.momoBorderBaseBytes = String(refinedTerrain ? COASTAL_BORDER_BASE_BYTES : 0);
    document.documentElement.dataset.momoEnvironment = "coastal-ready";
    document.documentElement.dataset.momoEnvironmentRevision = "2";
    document.documentElement.dataset.momoEnvironmentFrames = String(Object.keys(replacements).length);
    document.documentElement.dataset.momoPavementBaseBytes = String(256 * 256 * 4);
    try {
        await loadStylizedEnvironment(textures, renderer);
    } catch (error) {
        document.documentElement.dataset.momoStylizedStatus = "failed";
        console.warn("MOMO field-kit textures unavailable; coastal revision 2 remains active.", error);
    }
}

/** Shared fixed border materials, never loaded per player or per shoreline section. */
export function coastalBorder(name: keyof typeof COASTAL_BORDER_ASSETS): Texture | undefined {
    return ready ? borderTextures[name] : undefined;
}

/** Static geometry uses small repeating textures; no animation, filters or per-frame drawing. */
export function coastalGround(name: Ground, scale: number, color = 0xffffff): FillStyle | undefined {
    const texture = groundTextures[name];
    if (!ready || !texture) return undefined;
    const tileScale = (refinedTerrain ? groundTileWorldSize[name] : 64) * scale / texture.width;
    return { texture, textureSpace: "global", matrix: new Matrix().scale(tileScale, tileScale), color };
}

/** Existing grey road/concrete polygons retain their geometry, paint and markings. */
export function coastalPavement(color: ColorSource, scale: number): FillStyle | undefined {
    if (!ready || !pavement) return undefined;
    const rgb = new Color(color).toNumber();
    const r = rgb >> 16, g = (rgb >> 8) & 255, b = rgb & 255;
    if (Math.max(r, g, b) - Math.min(r, g, b) > 12 || r > 160) return undefined;
    // The painted asphalt is mid-grey; compensate its average tone to retain each definition's road shade.
    const channel = (value: number): number => Math.min(255, Math.round(value * 255 / 176));
    const tint = (channel(r) << 16) | (channel(g) << 8) | channel(b);
    return { texture: pavement, textureSpace: "global", matrix: new Matrix().scale(32 * scale / 256, 32 * scale / 256), color: tint };
}

/** Port-only palette: fine slate asphalt and weathered warm paint, with native lane geometry. */
export function coastalPortMaterial(color: ColorSource, scale: number): FillStyle | undefined {
    if (!ready || !portAsphalt || !portPaint) return undefined;
    const rgb = new Color(color).toNumber();
    if (rgb === 0xe6e6e6 || rgb === 0xa6b541) {
        return { texture: portPaint, textureSpace: "global", color: rgb === 0xe6e6e6 ? 0xd4d4bc : 0xc4ac64,
            matrix: new Matrix().scale(12 * scale / 128, 12 * scale / 128) };
    }
    const tint = { [0x666666]: 0xc2c6bf, [0x4d4d4d]: 0x989f97, [0x595959]: 0xa7b0a9 }[rgb];
    if (tint === undefined) return undefined;
    return { texture: portAsphalt, textureSpace: "global", color: tint,
        matrix: new Matrix().scale(64 * scale / 512, 64 * scale / 512) };
}
